import { useMemo, useState } from 'react';
import { Wifi, X } from 'lucide-react';
import { useTransport } from '@/store/transport';
import { formatDisplayName } from '@/lib/displayName';
import type { TransportEntry } from '@/store/transport';
import { AddWirelessDebugModal } from './AddWirelessDebugModal';

/**
 * Top-of-page banner that surfaces a 「为 {device} 启用无线调试」 nudge
 * for every USB-online device that doesn't yet have a wireless-debug
 * backup. Wired to the `needsWirelessPair` runtime flag on transport
 * store entries:
 *
 *   - Server's `UsbAdbBackupProvisioner` sets the flag after the
 *     Android-11+ mDNS handover fails for a fresh USB attach.
 *   - The flag is auto-cleared the moment a wireless transport is
 *     attached (mDNS pair or manual code pair) — see
 *     `DeviceRegistry.attachWirelessTransport`.
 *   - The user can also dismiss for the current browser session via
 *     the X button (sessionStorage-scoped).
 *
 * Multiple rows render stacked, one per device. The "一键引导" button
 * opens the three-tab pair modal with `oneclick` preselected (the
 * default), which immediately tries to launch the phone's
 * 「通过二维码配对」 page over the live USB link.
 *
 * Visibility: piggy-backs on the transport-events WS, which is
 * already visibility-filtered server-side — non-admins only see banners
 * for devices they own / public devices.
 */
export function WirelessPairBanner() {
  const bySerial = useTransport((s) => s.bySerial);
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    // Session-scoped so a hard refresh re-evaluates from the server
    // state but `dismiss` clicks survive route changes during the
    // user's current tab session.
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = sessionStorage.getItem('adh.wirelessPairBanner.dismissed');
      if (!raw) return new Set();
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) return new Set(parsed.filter((s) => typeof s === 'string'));
    } catch {
      /* ignore */
    }
    return new Set();
  });
  const [modalOpen, setModalOpen] = useState(false);

  const candidates = useMemo<TransportEntry[]>(() => {
    // De-dupe through Map: the store indexes entries by every transport
    // alias, so the same record can appear under multiple keys.
    const uniq = new Map<string, TransportEntry>();
    for (const e of Object.values(bySerial)) {
      if (!e.needsWirelessPair) continue;
      // Sanity: only surface for currently USB-active devices. Without
      // the cable up the user can't reach the phone over adb anyway,
      // and the autoswitcher couldn't have just failed.
      if (e.activeTransport?.kind !== 'usb') continue;
      // Already has a wireless transport queued (race between
      // mark/clear) — let it through; the entry will repaint itself
      // when the next mirror-readiness frame lands.
      uniq.set(e.serial, e);
    }
    return [...uniq.values()].filter((e) => !dismissed.has(e.serial));
  }, [bySerial, dismissed]);

  if (candidates.length === 0) return null;

  function dismiss(serial: string): void {
    setDismissed((cur) => {
      const next = new Set(cur);
      next.add(serial);
      try {
        sessionStorage.setItem(
          'adh.wirelessPairBanner.dismissed',
          JSON.stringify([...next])
        );
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  return (
    <>
      <div className="space-y-1 border-b border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-800 dark:text-amber-200">
        {candidates.map((entry) => (
          <BannerRow
            key={entry.serial}
            entry={entry}
            onLaunch={() => setModalOpen(true)}
            onDismiss={() => dismiss(entry.serial)}
          />
        ))}
      </div>
      <AddWirelessDebugModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}

function BannerRow({
  entry,
  onLaunch,
  onDismiss,
}: {
  entry: TransportEntry;
  onLaunch: () => void;
  onDismiss: () => void;
}) {
  // Reuse the same display-name precedence used everywhere else
  // (alias > meta.model > live model > serial) so the banner shows
  // "RMX3710" not "FERSJ7EYMBMJLNRW".
  const displayName = formatDisplayName(
    { serial: entry.serial },
    entry.serial
  );
  return (
    <div className="flex items-center gap-2">
      <Wifi className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        建议为 <strong className="font-medium">{displayName}</strong> 启用无线调试以建立 adb 备线
        <span className="ml-1 text-amber-700/80 dark:text-amber-300/80">
          （USB 拔出后镜像 / control / logcat 都会断）
        </span>
      </span>
      <button
        className="rounded border border-amber-600/40 bg-amber-500/20 px-2 py-0.5 text-[11px] font-medium hover:bg-amber-500/30"
        onClick={onLaunch}
      >
        一键引导
      </button>
      <button
        className="rounded p-0.5 text-amber-700/70 hover:bg-amber-500/20 dark:text-amber-300/70"
        onClick={onDismiss}
        title="本会话不再提示"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
